import * as React from 'react';
import axios from 'axios';
import { useHistory, useParams } from 'react-router-dom';
import { useAsync, useAsyncFn, useSearchParam } from 'react-use';
import { EmptyState, Spinner } from '@design-system/elements';
import { Button } from '@design-system/buttons';
import SignDocument from './SignDocument';

export default function GuardDocument() {
  const [state, renewAsyncLink] = useGuard();
  const [renovation, renewLink] = renewAsyncLink;
  const histoy = useHistory();

  if (state === 'bypass') return <SignDocument />;
  if (state === 'verifying')
    return (
      <div className="flex h-screen h-screen flex-1 items-center justify-center">
        <Spinner size="xl" />
      </div>
    );

  return (
    <div>
      {!renovation.value && (
        <EmptyState
          header="Tu invitación expiró"
          description="La invitación anterior ha caducado. Para recibir una nueva invitación, por favor haz clic en el botón de abajo"
          primaryAction={
            <Button
              isLoading={renovation.loading}
              color="#6344FF"
              className="text-white"
              variant="solid"
              onClick={() => {
                renewLink();
              }}>
              Enviar nueva invitación
            </Button>
          }></EmptyState>
      )}

      {renovation.value && (
        <EmptyState
          header="¡Tu invitación está lista!"
          description="Te hemos enviado una nueva a tu correo. Por favor, accede a través de ella."
          primaryAction={
            <Button
              color="#6344FF"
              className="text-white"
              variant="solid"
              onClick={() => {
                histoy.push('/');
              }}>
              Aceptar{' '}
            </Button>
          }></EmptyState>
      )}
    </div>
  );
}

const API_URL = process.env.REACT_APP_API_V3;

type LocationParams = Partial<{
  documentId: string;
  folderId: string;
  teamId: string;
}>;

async function fetchDocument(
  token: string | null,
  params: LocationParams,
): Promise<Record<string, any>> {
  return axios.get(
    API_URL +
    `/teams/${params.teamId}/folders/${params.folderId}/documents/${params.documentId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

type JwtPayload = {
  sub: string;
  email: string;
  scope: string[];
  exp: number;
  iat: number;
};

async function getInvitationId(token: string | null): Promise<string> {
  return new Promise((res, rej) => {
    try {
      const { scope }: JwtPayload = decodeJwt<JwtPayload>(token);
      const id = scope[0].split(':')[1];
      console.log(`invite_id ${id}`);
      res(id);
    } catch (error) {
      rej(error);
    }
  });
}

async function renewInvitationLink(
  token: string | null,
  params: LocationParams & { invite_id: string },
) {
  return axios.get(
    API_URL +
    `/teams/${params.teamId}/folders/${params.folderId}/documents/${params.documentId}/invites/${params.invite_id}/notification`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

type State = 'bypass' | 'verifying' | 'expired' | 'idle';

function useGuard() {
  const token = useSearchParam('token');
  const params = useParams<LocationParams>();

  const asyncSate = useAsync(async () => {
    const res = await fetchDocument(token, params);
    return res;
  }, [token, params]);

  const renewAsyncLink = useAsyncFn(async () => {
    const res = await getInvitationId(token).then((invite_id) =>
      renewInvitationLink(token, { ...params, invite_id }),
    );
    return res;
  }, [token, params]);

  const state: State =
    token === null ||
      (typeof asyncSate.value === 'object' && asyncSate.value !== null)
      ? 'bypass'
      : asyncSate.loading === true
        ? 'verifying'
        : asyncSate.error instanceof Error
          ? 'expired'
          : 'idle';

  // React.useEffect(() => {
  //   if (state === 'expired') renewLink();
  // }, [state]);

  return [state, renewAsyncLink] as const;
}

function decodeJwt<T = any>(token: string | null): T {
  if (!token) {
    throw new Error('Token is required');
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid token');
  }

  const payload = parts[1];
  if (!payload) {
    throw new Error('Invalid token payload');
  }

  const decodedPayload = JSON.parse(
    window.atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
  );
  return decodedPayload as T;
}
